import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Mail, Lock, User as UserIcon, ArrowLeft, Phone, KeyRound, ShieldCheck, Fingerprint } from "lucide-react";
import { loginWithPasskey, isBiometricAvailable } from "@/lib/webauthn";

function formatError(detail) {
  if (!detail) return "Une erreur est survenue. Reessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return "Une erreur est survenue.";
}

export default function AuthModal() {
  const { authModalOpen, closeAuthModal, loginWithGoogle, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Onboarding funnel: SMS -> abonnement -> annonce. Anyone without a chosen plan
  // (and not Pro) lands on the plan page right after authenticating. Admins skip.
  const routeAfterAuth = (u) => {
    if (!u || u.is_admin) return;
    const hasPlan = u.is_pro || u.plan === "free" || u.plan === "premium";
    if (!hasPlan || u.phone_verified !== true) {
      const next = location.pathname.startsWith("/choisir-abonnement") ? "/deposer" : (location.pathname || "/deposer");
      navigate(`/choisir-abonnement?next=${encodeURIComponent(next)}`);
    }
  };
  const [view, setView] = useState("tabs"); // "tabs" | "forgot"
  const [tab, setTab] = useState("phone"); // phone first (UEMOA priority)
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginTotp, setLoginTotp] = useState("");
  const [totpRequired, setTotpRequired] = useState(false);
  const [emailCodeRequired, setEmailCodeRequired] = useState(false);
  const [loginEmailCode, setLoginEmailCode] = useState("");
  const [resending, setResending] = useState(false);
  const resendEmailCode = async () => {
    setResending(true);
    try {
      const r = await axios.post(`${API}/auth/login/resend-email-code`, { email: loginEmail, password: loginPassword }, { withCredentials: true });
      setLoginEmailCode(""); toast.success(r.data.detail);
    } catch (err) { toast.error(err.response?.data?.detail || "Renvoi impossible"); }
    finally { setResending(false); }
  };
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  // Phone auth (UEMOA-first)
  const [phoneStep, setPhoneStep] = useState("phone"); // "phone" | "code"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneName, setPhoneName] = useState("");
  const [phoneNormalised, setPhoneNormalised] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneCooldown, setPhoneCooldown] = useState(0);
  const [phoneDevMode, setPhoneDevMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  useEffect(() => {
    if (authModalOpen) isBiometricAvailable().then(setPasskeyAvailable);
  }, [authModalOpen]);

  const onPasskeyLogin = async () => {
    setError(""); setLoading(true);
    try {
      await loginWithPasskey();
      const u = await refresh();
      toast.success("Connecte avec votre empreinte digitale");
      resetForms(); closeAuthModal(); routeAfterAuth(u);
    } catch (err) {
      const msg = err.message || "Connexion biometrique echouee";
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  // Cooldown countdown for phone OTP resend
  useEffect(() => {
    if (phoneCooldown <= 0) return;
    const t = setTimeout(() => setPhoneCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [phoneCooldown]);

  const resetForms = () => {
    setLoginEmail(""); setLoginPassword(""); setLoginTotp(""); setLoginEmailCode(""); setTotpRequired(false); setEmailCodeRequired(false);
    setRegName(""); setRegEmail(""); setRegPassword("");
    setForgotEmail("");
    setPhoneStep("phone"); setPhoneNumber(""); setPhoneName("");
    setPhoneNormalised(""); setPhoneCode(""); setPhoneCooldown(0);
    setPhoneDevMode(false);
    setError(""); setLoading(false);
    setView("tabs"); setTab("phone");
  };

  const onPhoneStart = async (e) => {
    if (e) e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/phone-start`, { phone: phoneNumber }, { withCredentials: true });
      const data = res.data || {};
      setPhoneNormalised(data.phone || phoneNumber);
      setPhoneCooldown(data.cooldown_seconds || 60);
      setPhoneDevMode(!!data.dev_mode);
      setPhoneStep("code");
      toast.success("Code envoye par SMS");
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const onPhoneLogin = async (e) => {
    if (e) e.preventDefault();
    setError(""); setLoading(true);
    // Defensive: read DOM value as fallback (Android SMS autofill workaround)
    const liveCode = (document.getElementById("phone-auth-code")?.value || "").replace(/\D/g, "").slice(0, 6);
    const code = phoneCode.length === 6 ? phoneCode : liveCode;
    if (code.length !== 6) {
      setLoading(false);
      setError("Entrez les 6 chiffres du code recu par SMS.");
      toast.error("Entrez les 6 chiffres du code recu par SMS.");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/auth/phone-login`,
        { phone: phoneNormalised, code, name: phoneName },
        { withCredentials: true },
      );
      const u = await refresh();
      if (res.data?.is_new) {
        toast.success("Bienvenue ! Vous avez 3 boosts IA gratuits a utiliser sur vos annonces.", { duration: 6000 });
      } else {
        toast.success("Connecte avec succes");
      }
      resetForms(); closeAuthModal(); routeAfterAuth(u);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = formatError(detail) || err.message;
      setError(msg); toast.error(msg);
      if (typeof detail === "string" && /expir|introuvable/i.test(detail)) {
        setPhoneStep("phone"); setPhoneCode("");
      }
    } finally { setLoading(false); }
  };

  const onSubmitLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword, ...(loginTotp ? { totp_code: loginTotp } : {}), ...(loginEmailCode ? { email_code: loginEmailCode } : {}) }, { withCredentials: true });
      const u = await refresh();
      toast.success("Connecte avec succes");
      resetForms(); closeAuthModal(); routeAfterAuth(u);
    } catch (err) {
      if (err.response?.data?.email_code_required || err.response?.data?.totp_required) {
        const d = err.response.data;
        setTotpRequired(true);
        if (d.email_code_required) setEmailCodeRequired(true);
        const msg = d.email_code_required && !loginEmailCode
          ? "Bastion : un code de connexion vient d'etre envoye sur votre email. Saisissez-le, puis votre code Authenticator."
          : d.email_code_required ? (d.detail || "Code email incorrect ou expire")
          : loginTotp ? "Code d'authentification incorrect" : "Compte protege : saisissez le code de votre application d'authentification";
        setError(msg); toast.message(msg);
        return;
      }
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const onSubmitRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email: regEmail, password: regPassword, name: regName }, { withCredentials: true });
      const u = await refresh();
      toast.success("Compte cree, bienvenue !");
      resetForms(); closeAuthModal(); routeAfterAuth(u);
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const onSubmitForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success("Si un compte existe avec cet email, un lien de reinitialisation vous a ete envoye.");
      resetForms(); closeAuthModal();
    } catch (err) {
      // Endpoint always returns 200, but network failures could happen.
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={(v) => { if (!v) { resetForms(); closeAuthModal(); } }}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white rounded-3xl" data-testid="auth-modal">
        <div className="bg-gradient-to-br from-orange-50 to-white px-7 pt-7 pb-4 border-b border-orange-100">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[var(--ink)]" data-testid="auth-modal-title">
              {view === "forgot" ? "Mot de passe oublie" : "Connectez-vous ou creez votre compte"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--ink-soft)]">
              {view === "forgot"
                ? "Entrez votre email — nous vous enverrons un lien pour choisir un nouveau mot de passe."
                : "Pour deposer une annonce, sauvegarder vos favoris et contacter les vendeurs."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 pb-7 pt-5">
          {view === "forgot" ? (
            <form onSubmit={onSubmitForgot} className="space-y-3" data-testid="auth-forgot-form">
              <Field icon={<Mail size={16} />} type="email" placeholder="vous@email.com" value={forgotEmail} onChange={setForgotEmail} testId="auth-forgot-email" required />
              {error && <p className="text-sm text-red-600 font-semibold" data-testid="auth-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="auth-forgot-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "Envoi..." : "M'envoyer le lien"}
              </button>
              <button type="button" onClick={() => { setView("tabs"); setError(""); }} className="text-sm text-[var(--ink-soft)] inline-flex items-center gap-1 hover:text-orange-600" data-testid="auth-forgot-back">
                <ArrowLeft size={14} /> Retour
              </button>
            </form>
          ) : (
            <>
              {passkeyAvailable && (
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={onPasskeyLogin}
                    disabled={loading}
                    className="w-full py-3 rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100/70 transition-colors font-bold text-emerald-700 inline-flex items-center justify-center gap-2"
                    data-testid="auth-passkey-login-btn"
                  >
                    <Fingerprint size={18} />
                    Se connecter avec l&apos;empreinte digitale
                  </button>
                  {!localStorage.getItem("fodindin_passkey_enrolled") && (
                    <p className="text-[10px] text-[var(--ink-soft)] text-center mt-1" data-testid="passkey-hint">
                      Disponible si vous avez deja active l&apos;empreinte dans Parametres → Securite biometrique.
                    </p>
                  )}
                </div>
              )}
              <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
                <TabsList className="grid grid-cols-3 w-full bg-orange-50 h-11 rounded-full p-1">
                  <TabsTrigger value="phone" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-orange-600 font-bold text-xs sm:text-sm" data-testid="auth-tab-phone">
                    <Phone size={13} className="inline mr-1" /> Telephone
                  </TabsTrigger>
                  <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-orange-600 font-bold text-xs sm:text-sm" data-testid="auth-tab-login">Email</TabsTrigger>
                  <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-orange-600 font-bold text-xs sm:text-sm" data-testid="auth-tab-register">Creer</TabsTrigger>
                </TabsList>

                <TabsContent value="phone" className="mt-5">
                  {phoneStep === "phone" ? (
                    <form onSubmit={onPhoneStart} className="space-y-3" data-testid="auth-phone-form">
                      <p className="text-xs text-[var(--ink-soft)] -mt-1">
                        <ShieldCheck size={12} className="inline mr-1 text-orange-600" />
                        <b>Recommande</b> : aucune adresse email requise. Code envoye par SMS.
                      </p>
                      <Field icon={<Phone size={16} />} type="tel" placeholder="07 12 34 56 78 ou +225 07 12 34 56 78" value={phoneNumber} onChange={setPhoneNumber} testId="auth-phone-input" required />
                      <Field icon={<UserIcon size={16} />} type="text" placeholder="Votre prenom (optionnel)" value={phoneName} onChange={setPhoneName} testId="auth-phone-name" />
                      {error && <p className="text-sm text-red-600 font-semibold" data-testid="auth-phone-error">{error}</p>}
                      <button type="submit" disabled={loading || !phoneNumber} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="auth-phone-send-btn">
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />}
                        {loading ? "Envoi..." : "M'envoyer le code par SMS"}
                      </button>
                      <p className="text-[11px] text-[var(--ink-soft)] text-center">
                        En continuant vous acceptez nos <a href="/cgu" className="underline">CGU</a>.
                      </p>
                    </form>
                  ) : (
                    <form onSubmit={onPhoneLogin} className="space-y-3" data-testid="auth-phone-code-form" noValidate>
                      <p className="text-xs text-[var(--ink-soft)] -mt-1">
                        Entrez le code recu par SMS sur <b>{phoneNormalised}</b>.
                      </p>
                      <label className="block">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"><KeyRound size={16} /></span>
                          <input
                            id="phone-auth-code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            value={phoneCode}
                            onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            onInput={(e) => {
                              const v = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                              if (v !== phoneCode) setPhoneCode(v);
                            }}
                            placeholder="123456"
                            required
                            autoFocus
                            data-testid="auth-phone-code-input"
                            className="w-full !pl-10 !py-3 rounded-2xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition tracking-[0.4em] font-mono text-lg"
                          />
                        </div>
                        {phoneDevMode && (
                          <p className="text-[11px] text-orange-700 mt-1 font-semibold">
                            Mode dev : code visible dans les logs serveur.
                          </p>
                        )}
                      </label>
                      {error && <p className="text-sm text-red-600 font-semibold" data-testid="auth-phone-error">{error}</p>}
                      <button type="submit" disabled={loading} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="auth-phone-verify-btn">
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                        {loading ? "Verification..." : "Verifier et me connecter"}
                      </button>
                      <div className="flex items-center justify-between pt-1">
                        <button type="button" onClick={() => { setPhoneStep("phone"); setError(""); setPhoneCode(""); }} className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1 hover:text-orange-600" data-testid="auth-phone-back-btn">
                          <ArrowLeft size={12} /> Changer de numero
                        </button>
                        <button type="button" onClick={() => onPhoneStart()} disabled={phoneCooldown > 0 || loading} className="text-xs font-semibold text-orange-600 hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed" data-testid="auth-phone-resend-btn">
                          {phoneCooldown > 0 ? `Renvoyer dans ${phoneCooldown}s` : "Renvoyer le code"}
                        </button>
                      </div>
                    </form>
                  )}
                </TabsContent>

                <TabsContent value="login" className="mt-5">
                  <form onSubmit={onSubmitLogin} className="space-y-3" data-testid="auth-login-form">
                    <Field icon={<Mail size={16} />} type="email" placeholder="vous@email.com" value={loginEmail} onChange={setLoginEmail} testId="auth-login-email" required />
                    <Field icon={<Lock size={16} />} type="password" placeholder="Mot de passe" value={loginPassword} onChange={setLoginPassword} testId="auth-login-password" required />
                    {emailCodeRequired && (
                      <>
                        <Field icon={<Mail size={16} />} type="text" placeholder="Code a 6 chiffres recu par email" value={loginEmailCode} onChange={setLoginEmailCode} testId="auth-login-email-code" required />
                        <button type="button" onClick={resendEmailCode} disabled={resending} className="text-xs font-semibold text-orange-600 hover:underline -mt-1 self-start disabled:opacity-50" data-testid="auth-login-resend-code">
                          {resending ? "Envoi…" : "Code expiré ? Renvoyer le code"}
                        </button>
                      </>
                    )}
                    {totpRequired && (
                      <Field icon={<Lock size={16} />} type="text" placeholder="Code a 6 chiffres (application d'authentification)" value={loginTotp} onChange={setLoginTotp} testId="auth-login-totp" required />
                    )}
                    {error && <p className="text-sm text-red-600 font-semibold" data-testid="auth-error">{error}</p>}
                    <button type="submit" disabled={loading} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="auth-login-submit">
                      {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                      {loading ? "Connexion..." : "Se connecter"}
                    </button>
                    <button type="button" onClick={() => { setView("forgot"); setError(""); setForgotEmail(loginEmail); }} className="text-xs text-orange-600 hover:underline font-semibold" data-testid="auth-forgot-link">
                      Mot de passe oublie ?
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="register" className="mt-5">
                  <form onSubmit={onSubmitRegister} className="space-y-3" data-testid="auth-register-form">
                    <Field icon={<UserIcon size={16} />} type="text" placeholder="Votre nom complet" value={regName} onChange={setRegName} testId="auth-register-name" required />
                    <Field icon={<Mail size={16} />} type="email" placeholder="vous@email.com" value={regEmail} onChange={setRegEmail} testId="auth-register-email" required />
                    <Field icon={<Lock size={16} />} type="password" placeholder="Mot de passe (8 caracteres min)" value={regPassword} onChange={setRegPassword} testId="auth-register-password" required minLength={8} />
                    {error && <p className="text-sm text-red-600 font-semibold" data-testid="auth-error">{error}</p>}
                    <button type="submit" disabled={loading} className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2" data-testid="auth-register-submit">
                      {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                      {loading ? "Creation..." : "Creer mon compte"}
                    </button>
                    <p className="text-[11px] text-[var(--ink-soft)] text-center">
                      En creant un compte vous acceptez nos <a href="/cgu" className="underline">CGU</a>.
                    </p>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[var(--line)]" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[var(--ink-soft)]">ou</span></div>
              </div>

              <button
                type="button"
                onClick={() => { closeAuthModal(); loginWithGoogle(); }}
                className="btn-secondary w-full !py-3 inline-flex items-center justify-center gap-2"
                data-testid="auth-google-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C4 19.98 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 4 4.02 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
                Continuer avec Google
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ icon, type, placeholder, value, onChange, testId, required, minLength }) {
  return (
    <label className="block">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]">{icon}</span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          data-testid={testId}
          className="w-full !pl-10 !py-3 rounded-2xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition"
        />
      </div>
    </label>
  );
}
